/***
*
*   PROFILE
*   Update the user profile or close the account
*
**********/

import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import Axios from 'axios';

import { AccountContext, Card, getPipelines,
    SettingNav, Animate, Message, ViewContext } from 'components/lib';

import { UpdateLostUnaccounted } from './update-lost-unaccounted';
import Style from './settings.module.scss';

import { Button as AntButton, Col, List, Row, Typography } from 'antd';
import { EyeOutlined } from '@ant-design/icons';


const { Paragraph, Text } = Typography;


export function Pipelines(){

    const viewContext = useContext(ViewContext);
    const { accountState, accountDispatch } = useContext(AccountContext);
    const [reveals, setReveals] = useState({});

    const prepareData = useCallback(async () => {
        try {
            await getPipelines(accountDispatch, {
                params: {
                    with_settings: 1
                }
            });

        } catch (err) {
            viewContext.handleError(err);
        }
    }, [viewContext, accountDispatch]);

    useEffect(() => {

        if (accountState.pipelines) {
            const revealOpts = accountState.pipelines.reduce((opts, obj) => {
                if (obj.settings) {
                    if (!opts[obj.settings.id]) opts[obj.settings.id] = false;
                }

                return opts;
            }, {});

            setReveals(revealOpts);
        }

    }, [accountState.pipelines]);

    useEffect(() => {

        prepareData();
        
    }, []);

    const removePipeline = async (item) => {
        try {
            const res = await Axios.delete('/api/pipeline', { data: { id: item.id } });
      
            if (res.status === 200) {
                viewContext.notification.show('Pipeline was removed', 'success', true);
                await prepareData();
            }          
          }
          catch (err){
            viewContext.handleError(err);
          }
    }

    const openEditPipelineSettings = (item) => {
        const isUpdate = item?.settings?.id;
        const formObj = {
            website_url: {
                label: "Website URL",
                type: "text",
                required: true,
                name: "website_url",
                id: "website_url",
                value: item?.settings?.website_url
            },
            username: {
                label: "User Name",
                type: "text",
                required: true,
                name: "username",
                id: "username",
                value: item?.settings?.username
            },
            password: {
                label: "Password",
                type: "text",
                required: true,
                name: "password",
                id: "password",
                value: item?.settings?.password
            },
            pipeline_id: {
                type: "hidden",
                name: "pipeline_id",
                id: "pipeline_id",
                value: item?.id
            }
        };

        if (isUpdate) {
            formObj['id'] = {
                type: "hidden",
                name: "id",
                id: "id",
                value: item?.settings?.id
            };
        }

        viewContext.modal.show({

            title: 'Edit Pipeline Information',
            form: formObj,
            buttonText: 'Submit',
            url: '/api/pipeline/account-setting',
            method: isUpdate ? 'PATCH' : 'POST'
      
          }, () => {
            prepareData();
          });
    }

    const openCreatPipelineModal = () => {
        viewContext.modal.show({

            title: 'Create Pipeline',
            form: {
                name: {
                    label: "Name",
                    type: "text",
                    required: true,
                    name: "name",
                    id: "name"
                },
            },
            buttonText: 'Submit',
            url: '/api/pipeline',
            method: 'POST'
      
          }, () => {
            prepareData();
          });
    }

    const openLostUnaccountedModal = (plObj) => {
        viewContext.modal.show({

            title: 'Update Lost / Unaccounted Amount',
            customForm: UpdateLostUnaccounted,
            formProps: {
                pipeline: plObj
            },
      
          }, () => {
            prepareData();
          });
    }

    const renderPipelines = (item) => {

        const renderSettingsData = (settings) => {
            return (
                <>
                    {settings.website_url &&
                        <li>
                            <Text className={Style.title} strong>Website:</Text>
                            <Text>{settings.website_url}</Text>
                        </li>
                    }
                    {settings.username &&
                        <li>
                            <Text className={Style.title} strong>User Name:</Text>
                            <Text>{settings.username}</Text>
                        </li> 
                    }
                    {settings.password &&
                        <li>
                            <div>
                            <Text className={[Style.title, Style.passwordTitle]} strong>Password:</Text>
                            <Paragraph className={[Style.title, Style.passwordTitle]}  copyable={{ text: settings.password }}>
                                {reveals[settings.id] ? settings.password : '**********'}
                            </Paragraph>
                            </div>
                            
                            <div className={[Style.title, Style.passwordTitle]}>
                                <AntButton 
                                    shape="circle" 
                                    icon={<EyeOutlined />} 
                                    size="small" 
                                    onClick={() => { 
                                        
                                        setReveals({...reveals, ...{[settings.id]: !reveals[settings.id]}}) 
                                    }}
                                />
                            </div>
                        </li> 
                    }
                </>
            );
        }

        return (
            <List.Item
                actions={[
                    <AntButton 
                        key="pipeline-setting-edit" 
                        onClick={() => openEditPipelineSettings(item)}>
                            Edit Details
                    </AntButton>,
                    <AntButton 
                        key="pipeline-setting-edit" 
                        onClick={() => openLostUnaccountedModal(item)}>
                            Update Lost / Unaccounted Amount
                    </AntButton>,
                    <AntButton 
                        key="pipeline-editt" 
                        onClick={async() => await removePipeline(item)}>
                            Delete
                    </AntButton>
                ]}
            >
                <ul className={Style.dataList}>
                    <li>
                        <Text>{item.name}</Text>
                    </li>
                    {item.settings && renderSettingsData(item.settings)}
                    {item.pipeline_unaccounted_amount &&
                        <li>
                            <Text className={Style.title} strong>Lost and Unaccounted Amount:</Text>
                            <Text>{item.pipeline_unaccounted_amount}</Text>
                        </li> 
                    }
                </ul>
            </List.Item>
        );
    }

    return (
        <Fragment>
          <SettingNav />
          <Animate>
            <Message
                closable
                title='Pipeline Options List'
                text={(
                    <p>
                        These are the pipeline options that are assigned to your account. You can edit each pipeline details here.
                    </p>
                )}
                type='info'
            />
            <Card
                loading={accountState.loading}
            >
                <Row gutter={[16, 16]}>
                    <Col offset={18} span={6}>
                        <AntButton
                            style={{ float: 'right', margin: '10px 0' }}
                            size="middle"
                            onClick={() => openCreatPipelineModal() }
                        >
                            Create Pipeline
                        </AntButton>
                    </Col>
                </Row>
                <List
                    size="small"
                    bordered
                    dataSource={accountState.pipelines}
                    renderItem={renderPipelines}
                />
            </Card>
          </Animate>
        </Fragment>
      );
}